import { inter } from 'assets/theme/theme';
import {
	Box, Card, CircularProgress, FormHelperText, Grid, InputLabel, Stack, Typography, useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAuthBackoffice from '../../provider/auth-backoffice.provider';
import { DefaultButton, SizedBox } from 'common/presentation/components';
import Dimens from 'assets/dimens';
import useTwoFactorBackoffice from '../../provider/two-factor-auth.provider';
import TextField from 'common/presentation/components/text-field/TextField';

const TwoFactorAuthentication = () => {
	const theme = useTheme();
	const { t } = useTranslation('translations');
	const { loggedUser } = useAuthBackoffice();
	const {
		fetchState,
		enableTwoFactorAuth,
		qrCodeUrl,
		setQrCodeUrl,
		confirmTwoFactorAuth,
	} = useTwoFactorBackoffice();

	const [otp, setOtp] = useState('');

	useEffect(() => {
		setQrCodeUrl('');
	}, []);

	const onEnableClick = () => {
		enableTwoFactorAuth(loggedUser?.userId ?? 0);
	};

	const handleConfirmClick = () => {
		confirmTwoFactorAuth(loggedUser?.userId ?? 0, otp);
	};

	const enableTwoFactorView = () => (
		<Box>
			<SizedBox height={2} />
			<DefaultButton
				sx={{
					borderRadius: Dimens.CARD_BORDER_RADIUS,
					textTransform: 'none',
					fontFamily: inter,
				}}
				onClick={onEnableClick}
				fullWidth
				disabled={fetchState.loading}
			>
				{!fetchState.loading && t('activate')}
				{fetchState.loading && <CircularProgress />}
			</DefaultButton>
			<SizedBox height={6} />
			<Typography
				color={theme.palette.textLight.main}
				fontSize={16}
				fontFamily={inter}
				fontWeight={600}
			>
				{t('two_factor_info')}
			</Typography>
		</Box>
	);

	const confirmTwoFactorView = () => (
		<Box>
			<Typography
				fontSize={18}
				fontFamily={inter}
				fontWeight={600}
			>
				{t('two_factor_step1')}
			</Typography>
			<SizedBox height={1} />
			<Typography
				color={theme.palette.textLight.main}
				fontSize={14}
				fontFamily={inter}
				fontWeight={600}
			>
				{t('two_factor_step1_info')}
			</Typography>
			<SizedBox height={2} />
			<Typography
				fontSize={18}
				fontFamily={inter}
				fontWeight={600}
			>
				{t('two_factor_step2')}
			</Typography>
			<SizedBox height={1} />
			<Typography
				color={theme.palette.textLight.main}
				fontSize={14}
				fontFamily={inter}
				fontWeight={600}
			>
				{t('two_factor_step2_info')}
			</Typography>
			<SizedBox height={2} />
			<Stack alignItems="center">
				<img src={qrCodeUrl} alt="2FA Qr code" />
			</Stack>
			<SizedBox height={4} />
			<Typography
				fontSize={18}
				fontFamily={inter}
				fontWeight={600}
			>
				{t('two_factor_step3')}
			</Typography>
			<SizedBox height={1} />
			<Typography
				color={theme.palette.textLight.main}
				fontSize={14}
				fontFamily={inter}
				fontWeight={600}
			>
				{t('two_factor_step3_info')}
			</Typography>
			<SizedBox height={2} />
			<div>
				<InputLabel shrink htmlFor="bootstrap-input">
					{t('code')}
				</InputLabel>
				<TextField
					fullWidth
					type="number"
					placeholder="code"
					value={otp}
					inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
					onChange={(e) => {
						const val = e.target.value;
						if (/^\d*$/.test(val) && val.length <= 6) {
							setOtp(val);
						}
					}}
				/>
			</div>
			{fetchState.error && (
				<FormHelperText error>{fetchState.error}</FormHelperText>
			)}
			<SizedBox height={2} />
			<DefaultButton
				fullWidth
				disabled={otp.length < 6}
				onClick={handleConfirmClick}
			>
				{!fetchState.loading && t('confirm')}
				{fetchState.loading && <CircularProgress size={26} />}
			</DefaultButton>
			<SizedBox height={4} />
			<Typography
				color={theme.palette.textLight.main}
				fontSize={16}
				fontFamily={inter}
			>
				{t('two_factor_steps_info')}
			</Typography>
		</Box>
	);

	return (
		<Box sx={{ pl: 2, pb: 2 }}>
			<Typography fontSize={22} fontFamily={inter} fontWeight={400}>
				{t('setup_two_factor_authentication')}
			</Typography>
			<Stack alignItems="center">
				<Grid container mt={2} justifyContent="center" spacing={2}>
					<Grid item md={4} xs={10}>
						<Card sx={{ p: 4 }}>
							<Stack spacing={4}>
								{!qrCodeUrl && (
									<Stack spacing={2} direction="row">
										<Typography
											color={theme.palette.textLight.main}
											fontSize={22}
											fontFamily={inter}
											fontWeight={400}
										>
											{t('two_factor_auth_status')}
										</Typography>
										<Typography
											color={
												loggedUser?.requiresTwoFactor
													? theme.palette.success.main : theme.palette.error.main
											}
											fontSize={22}
											fontFamily={inter}
											fontWeight={700}
											textTransform="uppercase"
										>
											{loggedUser?.requiresTwoFactor ? t('enabled') : t('disabled')}
										</Typography>
									</Stack>
								)}
								{!loggedUser?.requiresTwoFactor && (
									qrCodeUrl ? confirmTwoFactorView() : enableTwoFactorView()
								)}
								{loggedUser?.requiresTwoFactor && (
									<Typography
										color={theme.palette.textLight.main}
										fontSize={16}
										fontFamily={inter}
									>
										Para reconfigurar contacte um dos administradores
									</Typography>
								)}
							</Stack>
						</Card>
					</Grid>
				</Grid>
			</Stack>
		</Box>
	);
};

export default TwoFactorAuthentication;
